import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {cnb} from 'cnbuilder';
import {useSelector} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';
import IconAlertDefault from '@teladoc/pulse/icons/alert-default.svg';
import Image from '@teladoc/pulse/ui/Image';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import cssNoMatch from '../../router/NoMatch.scss';
import ScreenReader from '../screen-reader/ScreenReader';
import CommonUtils from '../utilities/common-utils';
import Card from '../card/Card';
import UserAPI from '../../user/user-api';
import {NETWORK_STATUS} from '../../config';
import css from './ApiError.scss';

// The error_title/error_message defaults below describe this component's own
// copy; callers override them, and supply the area name `error_type` (from
// ERROR_TRACKING_COMPONENTS), through amplitudeEventProps.
const ApiError = ({className, amplitudeEventName, amplitudeEventProps}) => {
    const {t} = useTranslation();
    const networkStatus = UserAPI.getNetworkStatus(NETWORK_STATUS);
    const {isOneApp} = useSelector(state => state.app);

    useEffect(() => {
        if (isOneApp && TRACK_EVENTS_MAP[amplitudeEventName]) {
            const supportEmail =
                window?.process?.env?.MEMBER_SUPPORT_EMAIL ??
                process.env.MEMBER_SUPPORT_EMAIL;
            const supportPhone =
                window?.process?.env?.MEMBER_SUPPORT_PHONE ??
                process.env.MEMBER_SUPPORT_PHONE;

            /* eslint-disable camelcase */
            AmplitudeClient.track(TRACK_EVENTS_MAP[amplitudeEventName], {
                error_title: 'We had a hiccup',
                error_message: `This page had an error. Try refreshing your browser. If it happens again, email us at ${supportEmail} or call ${supportPhone}`,
                ...amplitudeEventProps,
            });
            /* eslint-enable camelcase */
        }
    }, [isOneApp, amplitudeEventName, amplitudeEventProps]);

    return (
        <>
            {networkStatus && (
                <div className={cnb(css.root, className)}>
                    <IconAlertDefault className={css.icon} />
                    <div className={css.content}>
                        <h2 className="heading-sm" data-testid="heading">
                            {t('errors.api.title')}
                        </h2>
                        <p>
                            <Trans
                                i18nKey="app:errors.api.body"
                                components={[
                                    <a
                                        key={
                                            window?.process?.env
                                                ?.MEMBER_SUPPORT_EMAIL ??
                                            process.env.MEMBER_SUPPORT_EMAIL
                                        }
                                        href={`mailto:${
                                            window?.process?.env
                                                ?.MEMBER_SUPPORT_EMAIL ??
                                            process.env.MEMBER_SUPPORT_EMAIL
                                        }`}
                                    >
                                        <ScreenReader />
                                    </a>,
                                    <a
                                        key={
                                            window?.process?.env
                                                ?.MEMBER_SUPPORT_PHONE ??
                                            process.env.MEMBER_SUPPORT_PHONE
                                        }
                                        href={`tel:${
                                            window?.process?.env
                                                ?.MEMBER_SUPPORT_PHONE ??
                                            process.env.MEMBER_SUPPORT_PHONE
                                        }`}
                                    >
                                        <ScreenReader />
                                    </a>,
                                ]}
                                values={{
                                    email:
                                        window?.process?.env
                                            ?.MEMBER_SUPPORT_EMAIL ??
                                        process.env.MEMBER_SUPPORT_EMAIL,
                                    number: CommonUtils.formatPhoneNumber(
                                        window?.process?.env
                                            ?.MEMBER_SUPPORT_PHONE ??
                                            process.env.MEMBER_SUPPORT_PHONE
                                    ),
                                }}
                            />
                        </p>
                    </div>
                </div>
            )}
            {!networkStatus && (
                <div className={cnb(cssNoMatch.root, className)}>
                    <Card className={css.card} fullExtension>
                        <Image
                            classNameRoot={cssNoMatch.image}
                            cloudinaryImageId="member-portal/common/error_tcmgli"
                            width={164}
                            height={117}
                            alt=""
                        />
                        <h1 className="heading-xs">
                            {t('errors.missing.title')}
                        </h1>
                        <p>{t('errors.missing.body')}</p>
                    </Card>
                </div>
            )}
        </>
    );
};

ApiError.propTypes = {
    className: PropTypes.string,
    amplitudeEventName: PropTypes.string,
    amplitudeEventProps: PropTypes.object,
};

export default ApiError;
